import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { FinancialSnapshotAnalytics } from '../analytics/financial-snapshot-analytics';
import type { SnapshotStatus } from '../financial-snapshot.model';
import { FINANCIAL_SNAPSHOT_UI_ROW_COUNT } from '../mock/financial-snapshot-compute';

/**
 * DEP-2 readiness: AIM must deliver the Intercom send mechanism before this flips true.
 * Affordance is present; the API call stays no-op until then.
 */
export const FINANCIAL_SNAPSHOT_SEND_TO_CLIENT_READY = false;

@Injectable({ providedIn: 'root' })
export class FinancialSnapshotSendAction {
  private readonly _messages = inject(MessageService);
  private readonly _analytics = inject(FinancialSnapshotAnalytics);

  /**
   * Attempt send-to-client. Until DEP-2 lands, shows an info toast and returns without calling an API.
   */
  async send(params: {
    clientId: string;
    status: SnapshotStatus | null;
    enteredRowCount: number;
  }): Promise<void> {
    const completeness: 'complete' | 'incomplete' =
      params.status === 'complete' || params.status === 'outdated'
        ? 'complete'
        : params.enteredRowCount >= FINANCIAL_SNAPSHOT_UI_ROW_COUNT
          ? 'complete'
          : 'incomplete';

    if (!FINANCIAL_SNAPSHOT_SEND_TO_CLIENT_READY) {
      this._messages.add({
        severity: 'info',
        summary: 'Coming soon',
        detail: 'Send to client will be available once delivery is wired (DEP-2).',
      });
      return;
    }

    // DEP-2: call AIM send endpoint here, then:
    this._analytics.sentToClient(params.clientId, completeness);
    this._messages.add({
      severity: 'success',
      summary: 'Sent',
      detail: 'Financial snapshot link sent to client.',
    });
  }
}
